import {
  faExclamationTriangle,
  faPause,
  faPlay,
  faSpinner,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Div, Field, Label, Progress, Section } from "trunx";
import { LeafletPanelBody, LeafletPanelHeading } from "../shared/LeafletPanel";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useContext, useEffect, useRef } from "react";
import { useLocaleContext } from "../contexts/LocaleContext";
import WrfContext, { WrfProduct } from "../contexts/WrfContext";
import { useRootStore } from "../models/Root";

const ForecastPanel = () => {
  const {
    lang,
    dict: {
      panel: { forecast: dict },
    },
  } = useLocaleContext();

  const wrf = useContext(WrfContext);

  const {
    menus: { closePanel },
  } = useRootStore();

  // Auto-show the overlay once, when data first arrives after the panel opens.
  // Depend only on the data — NOT the whole `wrf` object (it is rebuilt every
  // animation tick) and NOT on `visible` — and guard with a ref so the user's
  // Stop (setVisible(false)) sticks instead of being immediately re-shown.
  const autoShownRef = useRef(false);
  useEffect(() => {
    if (wrf?.activeApiResponse && !autoShownRef.current) {
      autoShownRef.current = true;
      wrf.setVisible(true);
    }
  }, [wrf?.activeApiResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!wrf?.rainfallApiResponse && !wrf?.windApiResponse) {
    return (
      <Div bulma="panel">
        <Section>
          <LeafletPanelHeading title={dict.title} close={closePanel} />
        </Section>
        <div className="radar is-loading">&nbsp;</div>
      </Div>
    );
  }

  const {
    frame,
    setVisible,
    playing,
    setPlaying,
    speed,
    setSpeed,
    opacity,
    setOpacity,
    framesLoaded,
    framesTotal,
    product,
    setProduct,
    rainfallApiResponse,
    windApiResponse,
  } = wrf;
  const times = wrf.activeApiResponse?.times;
  const frameTime = times?.[frame];
  const isLoading = framesLoaded < framesTotal;

  // A current forecast's newest frame is a future valid-time; if the newest
  // frame is already in the past, the WRF feed hasn't updated — surface it.
  const newest =
    times && times.length
      ? new Date(Math.max(...times.map((t) => t.getTime())))
      : undefined;
  const isStale = !!newest && newest.getTime() < Date.now();
  const staleAge = (d: Date) => {
    const hrs = (Date.now() - d.getTime()) / 3600000;
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    return Math.round(hrs) >= 24
      ? rtf.format(-Math.round(hrs / 24), "day")
      : rtf.format(-Math.round(hrs), "hour");
  };

  const stop = () => {
    setPlaying(false);
    setVisible(false);
  };

  const playPause = () => {
    setPlaying(!playing);
    setVisible(true);
  };

  const ProductButton = ({ value }: { value: WrfProduct }) => {
    const isSelected = product === value;
    const isAvailable = !!(value === "rainfall"
      ? rainfallApiResponse
      : windApiResponse);
    return (
      <Button
        onClick={() => setProduct(value)}
        disabled={!isAvailable}
        className={classNames({
          "is-selected": isSelected,
          "is-primary": isSelected,
        })}
      >
        {dict[value]}
      </Button>
    );
  };

  return (
    <Div bulma="panel">
      <Div bulma="panel-heading">
        <LeafletPanelHeading title={dict.title} close={closePanel} />
      </Div>
      <LeafletPanelBody>
        <Section>
          <div
            className="buttons has-addons"
            style={{ justifyContent: "center" }}
          >
            <ProductButton value="rainfall" />
            <ProductButton value="wind" />
          </div>

          <Div bulma={["level", "is-mobile"]}>
            <Div bulma="level-left">
              <Div bulma={["level-item"]}>
                {isLoading ? (
                  <Div bulma="level-item" style={{ fontSize: "0.85em" }}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    &nbsp;
                    {framesLoaded}/{framesTotal}
                  </Div>
                ) : (
                  <>
                    <Div bulma="level-item" onClick={() => playPause()}>
                      <FontAwesomeIcon icon={playing ? faPause : faPlay} />
                    </Div>
                    &nbsp;
                    <Div bulma="level-item" onClick={() => stop()}>
                      <FontAwesomeIcon icon={faStop} />
                    </Div>
                  </>
                )}
              </Div>
            </Div>
            <Div bulma="level-item">
              {isLoading ? (
                <Progress
                  bulma="is-info"
                  value={framesLoaded}
                  max={framesTotal}
                />
              ) : (
                <Progress
                  value={Math.max(0, framesTotal - frame)}
                  max={framesTotal}
                />
              )}
            </Div>
            {!isLoading && (
              <Div bulma="level-right">
                <Div bulma={["level-item", "ml-2"]}>
                  {frameTime?.toLocaleTimeString(lang)}
                </Div>
              </Div>
            )}
          </Div>

          {!isLoading && isStale && frameTime && (
            <Div
              bulma={["level", "is-mobile"]}
              style={{ marginTop: "-0.5rem", fontSize: "0.85em" }}
            >
              <Div bulma="level-left">
                <Div bulma="level-item">
                  {frameTime.toLocaleDateString(lang, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                  {" · "}
                  {frameTime.toLocaleTimeString(lang, {
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </Div>
              </Div>
              <Div bulma="level-right">
                <span className="tag is-warning is-light">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  &nbsp;
                  {newest && staleAge(newest)}
                </span>
              </Div>
            </Div>
          )}

          <Field bulma="is-horizontal">
            <Div bulma="field-label">
              <Label>{dict.opacity}</Label>
            </Div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    style={{ width: "80%" }}
                    step="1"
                    min="1"
                    max="100"
                    type="range"
                    value={opacity * 100}
                    onChange={({ target }) =>
                      setOpacity(parseInt(target.value) / 100)
                    }
                  ></input>
                </div>
              </div>
            </div>
          </Field>

          <Field bulma="is-horizontal">
            <Div bulma="field-label">
              <Label>{dict.speed}</Label>
            </Div>
            <div className="field-body">
              <div className="field">
                <div className="control">
                  <input
                    style={{ width: "80%" }}
                    step="100"
                    min="0"
                    max="1000"
                    type="range"
                    value={speed}
                    onChange={({ target }) => setSpeed(parseInt(target.value))}
                  ></input>
                </div>
              </div>
            </div>
          </Field>
        </Section>
      </LeafletPanelBody>
    </Div>
  );
};
export default ForecastPanel;
